import { Download } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { guardarArchivo } from '@/lib/descargar';
import { fechaHora } from '@/lib/formato';

import { useOpiniones } from './queries';

const CLAVES = ['from', 'to', 'area', 'q'] as const;

/** Campos de fecha y búsqueda comunes a Opiniones y Seguridad. */
export function FiltrosFechas({
  filtros,
  cambiar,
}: {
  filtros: { from: string; to: string; q: string };
  cambiar: (c: Record<string, string>) => void;
}) {
  return (
    <>
      <Campo etiqueta="Desde">
        <input
          type="date"
          className="input-vidrio"
          value={filtros.from}
          onChange={(e) => cambiar({ from: e.target.value, to: filtros.to || e.target.value })}
        />
      </Campo>
      <Campo etiqueta="Hasta">
        <input
          type="date"
          className="input-vidrio"
          value={filtros.to}
          onChange={(e) => cambiar({ to: e.target.value, from: filtros.from || e.target.value })}
        />
      </Campo>
      <Campo etiqueta="Alumno">
        <input
          className="input-vidrio"
          placeholder="Nombre o correo"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value })}
          onBlur={(e) => e.target.value !== filtros.q && cambiar({ q: e.target.value })}
        />
      </Campo>
    </>
  );
}

function Opiniones() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const q = useOpiniones({ ...query, page: filtros.page, pageSize: 50 });
  const { proteger, dialogo } = useStepUp();
  const [exportando, setExportando] = useState(false);
  const exportar = async () => {
    setExportando(true);
    try {
      await proteger(async () =>
        guardarArchivo(
          await api.GET('/api/v1/admin/feedback/export', { params: { query }, parseAs: 'blob' }),
          'opiniones.xlsx',
        ),
      );
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setExportando(false);
    }
  };
  const d = q.data?.data;
  return (
    <>
      <Encabezado
        titulo="Opiniones de alumnos"
        descripcion="Lo que los alumnos escriben en el buzón de la plataforma."
        acciones={
          <button
            type="button"
            className="btn-cs btn-contorno"
            disabled={exportando}
            onClick={() => void exportar()}
          >
            <Download size={16} aria-hidden /> Exportar .xlsx
          </button>
        }
      />
      <div className="panel-vidrio mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FiltrosFechas filtros={filtros} cambiar={cambiar} />
        <Campo etiqueta="Área">
          <select
            className="input-vidrio"
            value={filtros.area}
            onChange={(e) => cambiar({ area: e.target.value })}
          >
            <option value="">Todas</option>
            {d?.areas.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {d?.items.length === 0 && <Vacio titulo="Sin opiniones con esos filtros" />}
      <ul className="space-y-2">
        {d?.items.map((o) => (
          <li key={o.id} className="panel-vidrio py-3">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-texto-3">
              <Link
                to={`/alumnos/${o.userId}`}
                className="font-semibold text-texto hover:underline"
              >
                {o.userName}
              </Link>
              <span>{o.userEmail}</span>
              <span className="insignia">{o.area}</span>
              <span className="ml-auto">{o.createdAt && fechaHora(o.createdAt)}</span>
            </div>
            <p className="text-sm whitespace-pre-wrap">{o.body}</p>
          </li>
        ))}
      </ul>
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      {dialogo}
    </>
  );
}

export function OpinionesPage() {
  return (
    <PermissionGate seccion="reportes">
      <Opiniones />
    </PermissionGate>
  );
}
